import {
  getScrollEventTarget,
  getScrollTop,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * Floating back-to-top control that respects Horizon's scroll container.
 *
 * @extends {HTMLElement}
 */
class BackToTop extends HTMLElement {
  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#bindScrollTarget();
    this.addEventListener('click', this.#handleClick);
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#updateVisibility();
  }

  disconnectedCallback() {
    this.#unbindScrollTarget();
    this.removeEventListener('click', this.#handleClick);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  get #threshold() {
    const value = Number(this.dataset.threshold);
    return Number.isFinite(value) ? value : 400;
  }

  #bindScrollTarget = () => {
    this.#unbindScrollTarget();
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  };

  #unbindScrollTarget = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = null;
  };

  #handleBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#updateVisibility();
  };

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateVisibility();
    });
  };

  #updateVisibility = () => {
    const shouldShow = getScrollTop() >= this.#threshold;
    this.toggleAttribute('data-visible', shouldShow);
    this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    if (this.#button) {
      this.#button.tabIndex = shouldShow ? 0 : -1;
    }
  };

  #handleClick = () => {
    scrollTo({ top: 0, behavior: 'smooth' });
  };
}

if (!customElements.get('back-to-top')) {
  customElements.define('back-to-top', BackToTop);
}
